import { fetchAuthSession } from "aws-amplify/auth";
import amplifyOutputs from "@/amplify_outputs.json";

const MFA_MANAGEMENT_FUNCTION_URL = amplifyOutputs.custom?.mfaManagementFunctionUrl || "";

const getAuthHeaders = async () => {
  try {
    const session = await fetchAuthSession();
    const accessToken = session.tokens.accessToken.toString();
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    };
  } catch (error) {
    console.error("Error getting auth session:", error);
    throw new Error("Authentication required");
  }
};

export const associateSoftwareToken = async () => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(MFA_MANAGEMENT_FUNCTION_URL, {
      method: "POST",
      headers,
      body: JSON.stringify({
        action: "ASSOCIATE_SOFTWARE_TOKEN",
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Failed to associate software token");
    }

    return data;
  } catch (error) {
    console.error("Error associating software token:", error);
    throw error;
  }
};

export const verifySoftwareToken = async (mfaCode) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(MFA_MANAGEMENT_FUNCTION_URL, {
      method: "POST",
      headers,
      body: JSON.stringify({
        action: "VERIFY_SOFTWARE_TOKEN",
        mfaCode: mfaCode,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Failed to verify software token");
    }

    return data;
  } catch (error) {
    console.error("Error verifying software token:", error);
    throw error;
  }
};

export const setMFAPreference = async () => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(MFA_MANAGEMENT_FUNCTION_URL, {
      method: "POST",
      headers,
      body: JSON.stringify({
        action: "SET_MFA_PREFERENCE",
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Failed to set MFA preference");
    }

    return data;
  } catch (error) {
    console.error("Error setting MFA preference:", error);
    throw error;
  }
};

export const getMFAStatus = async () => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(MFA_MANAGEMENT_FUNCTION_URL, {
      method: "POST",
      headers,
      body: JSON.stringify({
        action: "GET_MFA_STATUS",
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Failed to get MFA status");
    }

    return data;
  } catch (error) {
    console.error("Error getting MFA status:", error);
    throw error;
  }
};

export const disableMFA = async () => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(MFA_MANAGEMENT_FUNCTION_URL, {
      method: "POST",
      headers,
      body: JSON.stringify({
        action: "DISABLE_MFA",
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Failed to disable MFA");
    }

    return data;
  } catch (error) {
    console.error("Error disabling MFA:", error);
    throw error;
  }
};

export const adminGetUserMFAStatus = async (targetUserEmail) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(MFA_MANAGEMENT_FUNCTION_URL, {
      method: "POST",
      headers,
      body: JSON.stringify({
        action: "ADMIN_GET_USER_MFA_STATUS",
        targetUserEmail: targetUserEmail,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Failed to get user MFA status");
    }

    return data;
  } catch (error) {
    console.error("Error getting user MFA status:", error);
    throw error;
  }
};

export const adminDisableUserMFA = async (targetUserEmail) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(MFA_MANAGEMENT_FUNCTION_URL, {
      method: "POST",
      headers,
      body: JSON.stringify({
        action: "ADMIN_DISABLE_USER_MFA",
        targetUserEmail: targetUserEmail,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Failed to disable user MFA");
    }

    return data;
  } catch (error) {
    console.error("Error disabling user MFA:", error);
    throw error;
  }
};

export const adminEnableUserMFA = async (targetUserEmail) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(MFA_MANAGEMENT_FUNCTION_URL, {
      method: "POST",
      headers,
      body: JSON.stringify({
        action: "ADMIN_ENABLE_USER_MFA",
        targetUserEmail: targetUserEmail,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Failed to enable user MFA");
    }

    return data;
  } catch (error) {
    console.error("Error enabling user MFA:", error);
    throw error;
  }
};

export const recordLogin = async () => {
  try {
    const headers = await getAuthHeaders();
    await fetch(MFA_MANAGEMENT_FUNCTION_URL, {
      method: "POST",
      headers,
      body: JSON.stringify({ action: "RECORD_LOGIN" }),
    });
  } catch (error) {
    console.warn("Could not record login:", error);
  }
};

// Helper function to generate QR code for TOTP setup
export const generateQRCode = (secret, email) => {
  const issuer = "Trivista";
  const accountName = email;
  const secretKey = secret;

  const totpUri = `otpauth://totp/${encodeURIComponent(accountName)}?secret=${secretKey}&issuer=${encodeURIComponent(issuer)}`;

  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(totpUri)}`;

  return {
    qrCodeUrl,
    totpUri,
    secretKey,
    manualEntryKey: secretKey,
  };
};